import { ClientInfoStatus } from '../interfaces/client'
import { ClientEntityRelationshipType } from '../interfaces/integration'

export const CLIENT_DEFAULT_WIRE_FEE = 25
export const CLIENT_DEFAULT_LOCKBOX_FEE = 150
export const CLIENT_DEFAULT_SERVICING_FEE = 2500
export const CLIENT_DEFAULT_GRACE_PERIOD_DAYS = 60
export const CLIENT_DEFAULT_INTEREST_PER_DAYS = 360
export const CLIENT_DEFAULT_PAST_INVOICE_THRESHOLD = 90

export const LOAN_TYPES = {
  ar: 'AR',
  arAndInventory: 'AR & Inventory',
  inventory: 'Inventory',
}

export const ASSET_TYPES = {
  ar: 'AR',
  inventory: 'Inventory',
  equipment: 'Equipment',
}

export const LOAN_TYPE_MAP: Record<string, string> = {
  'AR and Inventory': LOAN_TYPES.arAndInventory,
  'AR Only': LOAN_TYPES.ar,
  'Inventory Only': LOAN_TYPES.inventory,
}

export const FREQUENCY_TYPES = {
  atFunding: 'At Funding',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiAnnually: 'Semi-Annually',
  annually: 'Annually',
  adHoc: 'Ad Hoc',
}

/** What we file under UCC is the search result, so screens say "UCC Search"; the stored name stays 'UCC'. */
export const UCC_SEARCH_LABEL = 'UCC Search'

export const LEGAL_DOCUMENT_TYPES = {
  ucc: 'UCC',
  gs: 'GS',
  coi: 'COI',
  eop: 'EOP',
  lsa: 'LSA',
  ipsa: 'IPSA',
  noticeOfIpsa: 'Notice of IPSA',
  lienFilings: 'Lien Filings',
  corporateGuaranteesAndPledges: 'Corporate Guarantees and Pledges',
  supportGuarantee: 'Support Guarantee',
  subordinationAgreements: 'Subordination Agreements',
  warehouseWaivers: 'Warehouse Waivers',
  signatureAuths: 'Signature Auths',
  boardConsent: 'Board Consent',
}

export const LEGAL_DASHBOARD_DOCUMENT_ORDER: string[] = [
  LEGAL_DOCUMENT_TYPES.ucc,
  LEGAL_DOCUMENT_TYPES.gs,
  LEGAL_DOCUMENT_TYPES.signatureAuths,
  LEGAL_DOCUMENT_TYPES.lienFilings,
  LEGAL_DOCUMENT_TYPES.lsa,
  LEGAL_DOCUMENT_TYPES.ipsa,
  LEGAL_DOCUMENT_TYPES.noticeOfIpsa,
  LEGAL_DOCUMENT_TYPES.subordinationAgreements,
  LEGAL_DOCUMENT_TYPES.supportGuarantee,
  LEGAL_DOCUMENT_TYPES.corporateGuaranteesAndPledges,
  LEGAL_DOCUMENT_TYPES.boardConsent,
]

export const LEGAL_REPORTING_ROW_ORDER: string[] = [
  LEGAL_DOCUMENT_TYPES.ucc,
  LEGAL_DOCUMENT_TYPES.gs,
  LEGAL_DOCUMENT_TYPES.coi,
  LEGAL_DOCUMENT_TYPES.eop,
  LEGAL_DOCUMENT_TYPES.warehouseWaivers,
  LEGAL_DOCUMENT_TYPES.signatureAuths,
  LEGAL_DOCUMENT_TYPES.lienFilings,
  LEGAL_DOCUMENT_TYPES.lsa,
  LEGAL_DOCUMENT_TYPES.ipsa,
  LEGAL_DOCUMENT_TYPES.noticeOfIpsa,
  LEGAL_DOCUMENT_TYPES.boardConsent,
  LEGAL_DOCUMENT_TYPES.subordinationAgreements,
  LEGAL_DOCUMENT_TYPES.supportGuarantee,
  LEGAL_DOCUMENT_TYPES.corporateGuaranteesAndPledges,
]

export const ADMIN_LEGAL_DOC_TYPES: ReadonlySet<string> = new Set([
  LEGAL_DOCUMENT_TYPES.lsa,
  LEGAL_DOCUMENT_TYPES.ipsa,
  LEGAL_DOCUMENT_TYPES.noticeOfIpsa,
  LEGAL_DOCUMENT_TYPES.boardConsent,
  LEGAL_DOCUMENT_TYPES.subordinationAgreements,
  LEGAL_DOCUMENT_TYPES.supportGuarantee,
  LEGAL_DOCUMENT_TYPES.corporateGuaranteesAndPledges,
])

export const REPORTING_DOCUMENT_TYPES = {
  apAging: 'AP Aging',
  arAging: 'AR Aging',
  inventoryDetail: 'Inventory Detail',
  financials: 'Financials',
  financialProjections: 'Financial Projections',
  salesBySKU: 'Sales By SKU',
  arGeneralLedger: 'AR General Ledger',
  bankTransactions: 'Bank Transactions',
  capTable: 'Cap Table',
  ...LEGAL_DOCUMENT_TYPES,
}

/**
 * Types that are borrowing-base inputs rather than ongoing-reporting records —
 * they get mapped into a BBC, never filed on the Analysis Queue. They have no
 * entry in `REPORTING_DOCUMENT_TYPES_TARGET_FOLDER_PATH` (so they'd land in
 * MiscReporting) and are filtered out of the queue's type options, which is why
 * they must never be an automated filing destination either.
 */
export const BORROWING_BASE_INPUT_TYPES: string[] = [
  REPORTING_DOCUMENT_TYPES.arAging,
  REPORTING_DOCUMENT_TYPES.apAging,
  REPORTING_DOCUMENT_TYPES.inventoryDetail,
]

export const PREPROCESSED_REPORTING_TYPES: string[] = [
  REPORTING_DOCUMENT_TYPES.financials,
  REPORTING_DOCUMENT_TYPES.financialProjections,
  REPORTING_DOCUMENT_TYPES.arGeneralLedger,
  REPORTING_DOCUMENT_TYPES.capTable,
  REPORTING_DOCUMENT_TYPES.bankTransactions,
]

export const RECURRING_REPORTING_TYPES: string[] = [
  REPORTING_DOCUMENT_TYPES.financials,
  REPORTING_DOCUMENT_TYPES.financialProjections,
  REPORTING_DOCUMENT_TYPES.salesBySKU,
  REPORTING_DOCUMENT_TYPES.arGeneralLedger,
  REPORTING_DOCUMENT_TYPES.bankTransactions,
  REPORTING_DOCUMENT_TYPES.capTable,
]

export const FINANCIALS_REPORTING_ROW_ORDER: string[] = [
  REPORTING_DOCUMENT_TYPES.financials,
  REPORTING_DOCUMENT_TYPES.financialProjections,
  REPORTING_DOCUMENT_TYPES.salesBySKU,
  REPORTING_DOCUMENT_TYPES.arGeneralLedger,
  REPORTING_DOCUMENT_TYPES.bankTransactions,
  REPORTING_DOCUMENT_TYPES.capTable,
]

export const UCC_RESULTS_OPTIONS = {
  entityNotFound: 'Entity Not Found',
  noLiensFound: 'No Liens Found',
  liensFoundNoIssues: 'Liens Found with No Issues',
  liensFoundWithIssues: 'Liens Found with Issues',
} as const

export const GS_STANDING_OPTIONS = {
  inGoodStanding: 'In Good Standing',
  notInGoodStanding: 'Not in Good Standing',
  dissolved: 'Dissolved',
  convertedRenamed: 'Converted/Renamed',
  entityNotFound: 'Entity Not Found',
} as const

/**
 * Non-compliant results, keyed by document type. `Entity Not Found` is non-compliant
 * for GS but compliant for UCC, so these cannot be a single flat set.
 */
export const NON_COMPLIANT_UCC_RESULTS: ReadonlySet<string> = new Set<string>([
  UCC_RESULTS_OPTIONS.liensFoundWithIssues,
])

export const NON_COMPLIANT_GS_RESULTS: ReadonlySet<string> = new Set<string>([
  GS_STANDING_OPTIONS.notInGoodStanding,
  GS_STANDING_OPTIONS.dissolved,
  GS_STANDING_OPTIONS.entityNotFound,
])

export const VALID_UCC_RESULTS: ReadonlySet<string> = new Set<string>(
  Object.values(UCC_RESULTS_OPTIONS),
)

export const VALID_GS_RESULTS: ReadonlySet<string> = new Set<string>(
  Object.values(GS_STANDING_OPTIONS),
)

export const UCC_RESULTS_FILE_SUFFIX: Readonly<Record<string, string>> = Object.fromEntries(
  Object.values(UCC_RESULTS_OPTIONS).map((v) => [v, v]),
)

export const GS_STANDING_FILE_SUFFIX: Readonly<Record<string, string>> = {
  ...Object.fromEntries(Object.values(GS_STANDING_OPTIONS).map((v) => [v, v])),
  // A slash would be read as a path separator by Box.
  [GS_STANDING_OPTIONS.convertedRenamed]: 'Converted-Renamed',
}

export const LEGAL_DOCUMENT_FOLDER_NAME: Partial<Record<string, string>> = {
  [REPORTING_DOCUMENT_TYPES.ucc]: 'UCC Searches',
  [REPORTING_DOCUMENT_TYPES.gs]: 'Good Standing',
  [REPORTING_DOCUMENT_TYPES.coi]: 'Company Insurance',
  [REPORTING_DOCUMENT_TYPES.eop]: 'Company Insurance',
  [REPORTING_DOCUMENT_TYPES.warehouseWaivers]: 'Warehouse Waivers',
  [REPORTING_DOCUMENT_TYPES.subordinationAgreements]: 'Subordination Agreements',
}

export const LEGAL_DOCUMENT_TYPES_ONGOING_REPORTING_MULTIPART = [
  REPORTING_DOCUMENT_TYPES.ucc,
  REPORTING_DOCUMENT_TYPES.gs,
  REPORTING_DOCUMENT_TYPES.coi,
  REPORTING_DOCUMENT_TYPES.eop,
] as const

export function isLegalDocumentOngoingReportingMultipart(documentName: string): boolean {
  return LEGAL_DOCUMENT_TYPES_ONGOING_REPORTING_MULTIPART.includes(documentName)
}

/** Document types that can be waived per legal entity + state without waiving the whole entity. */
export const LEGAL_ENTITY_WAIVABLE_DOCUMENT_TYPES = [REPORTING_DOCUMENT_TYPES.gs] as const

export function isUccOrGsOngoingReportingMultipart(documentName: string): boolean {
  return (
    documentName === REPORTING_DOCUMENT_TYPES.ucc || documentName === REPORTING_DOCUMENT_TYPES.gs
  )
}

/** Legal types that are not in `LEGAL_DOCUMENT_TYPES_ONGOING_REPORTING_MULTIPART` (no multipart sidebar). */
export const LEGAL_DOCUMENT_TYPES_EXCEPT_ONGOING_REPORTING_MULTIPART = Object.values(
  LEGAL_DOCUMENT_TYPES,
).filter((type) => !isLegalDocumentOngoingReportingMultipart(type))

export const ANALYSIS_QUEUE_TYPES = Object.values(REPORTING_DOCUMENT_TYPES).filter(
  (type) =>
    ![
      ...BORROWING_BASE_INPUT_TYPES,
      ...LEGAL_DOCUMENT_TYPES_EXCEPT_ONGOING_REPORTING_MULTIPART,
    ].includes(type),
)

export const FINANCIALS_COMPLIANCE_STATUS_DOCUMENT_TYPES = Object.values(
  REPORTING_DOCUMENT_TYPES,
).filter(
  (type) => ![...BORROWING_BASE_INPUT_TYPES, ...Object.values(LEGAL_DOCUMENT_TYPES)].includes(type),
)

export enum GrowthViewMode {
  Hide = 'hide',
  Amount = 'amount',
  Percent = 'percent',
}

/** Amount/Percent only — used where growth chips are shown (not Hide). */
export type GrowthChipViewMode = Exclude<GrowthViewMode, GrowthViewMode.Hide>

export const GROWTH_VIEW_OPTIONS = [
  { value: GrowthViewMode.Hide, label: 'Hide' },
  { value: GrowthViewMode.Amount, label: 'Amount' },
  { value: GrowthViewMode.Percent, label: 'Percent' },
]

export enum ReportingDocumentStatuses {
  InReview = 'in review',
  Due = 'due',
  Overdue = 'overdue',
}

export const statusProperties = [
  'clientIncomeStatementStatus',
  'projectionsStatus',
  'salesBySKUStatus',
  'arLedgerStatus',
  'bankTransactionsStatus',
  'coiExpirationStatus',
  'borrowingBaseStatus',
  'capTableStatus',
  'eopExpirationStatus',
]

export const statusToDateMapping = {
  clientIncomeStatementStatus: 'clientIncomeStatement',
  bankTransactionsStatus: 'bankTransactions',
  projectionsStatus: 'projections',
  salesBySKUStatus: 'salesBySKU',
  arLedgerStatus: 'arLedger',
  capTableStatus: 'capTable',
  coiExpirationStatus: 'coiExpiration',
  eopExpirationStatus: 'eopExpiration',
  borrowingBaseStatus: 'borrowingBase',
}

export const REPORTING_DOCUMENT_TYPES_BASE_FOLDER_PATH = {
  [REPORTING_DOCUMENT_TYPES.financials]: 'Financials/Actuals',
  [REPORTING_DOCUMENT_TYPES.financialProjections]: 'Financials/Projections',
  [REPORTING_DOCUMENT_TYPES.salesBySKU]: 'SalesBySKU',
  [REPORTING_DOCUMENT_TYPES.arGeneralLedger]: 'GeneralLedger',
  [REPORTING_DOCUMENT_TYPES.bankTransactions]: 'BankStatements',
  [REPORTING_DOCUMENT_TYPES.capTable]: 'CapTables',
  default: 'MiscReporting',
}

export const REPORTING_DOCUMENT_TYPES_TARGET_FOLDER_PATH = {
  [REPORTING_DOCUMENT_TYPES.financials]: 'Financials/Actuals/In Progress',
  [REPORTING_DOCUMENT_TYPES.financialProjections]: 'Financials/Projections/In Progress',
  [REPORTING_DOCUMENT_TYPES.salesBySKU]: 'SalesBySKU/In Progress',
  [REPORTING_DOCUMENT_TYPES.arGeneralLedger]: 'GeneralLedger/In Progress',
  [REPORTING_DOCUMENT_TYPES.bankTransactions]: 'BankStatements/In Progress',
  [REPORTING_DOCUMENT_TYPES.capTable]: 'CapTables/In Progress',
  [REPORTING_DOCUMENT_TYPES.ucc]: 'MiscReporting/UCC',
  [REPORTING_DOCUMENT_TYPES.gs]: 'MiscReporting/GS',
  default: 'MiscReporting',
}

export const REPORTING_DOCUMENTS_TITLES = {
  [REPORTING_DOCUMENT_TYPES.apAging]: 'Payables Aging',
  [REPORTING_DOCUMENT_TYPES.arAging]: 'Receivables Aging',
  [REPORTING_DOCUMENT_TYPES.inventoryDetail]: 'Inventory Detail',
  [REPORTING_DOCUMENT_TYPES.arGeneralLedger]: 'AR General Ledger',
  [REPORTING_DOCUMENT_TYPES.salesBySKU]: 'Sales By SKU',
  [REPORTING_DOCUMENT_TYPES.financials]: 'Financials',
  [REPORTING_DOCUMENT_TYPES.financialProjections]: 'Financial Projections',
  [REPORTING_DOCUMENT_TYPES.capTable]: 'Cap Table',
  [REPORTING_DOCUMENT_TYPES.bankTransactions]: 'Bank Transactions',
  [REPORTING_DOCUMENT_TYPES.coi]: 'Certificate of Liability Insurance',
  [REPORTING_DOCUMENT_TYPES.eop]: 'Evidence of Property Insurance',
  [REPORTING_DOCUMENT_TYPES.ucc]: UCC_SEARCH_LABEL,
  [REPORTING_DOCUMENT_TYPES.gs]: 'GS',
  [REPORTING_DOCUMENT_TYPES.lsa]: 'LSA',
  [REPORTING_DOCUMENT_TYPES.ipsa]: 'IPSA',
  [REPORTING_DOCUMENT_TYPES.noticeOfIpsa]: 'Notice of IPSA',
  [REPORTING_DOCUMENT_TYPES.lienFilings]: 'Lien Filings',
  [REPORTING_DOCUMENT_TYPES.corporateGuaranteesAndPledges]: 'Corporate Guarantees and Pledges',
  [REPORTING_DOCUMENT_TYPES.supportGuarantee]: 'Support Guarantee',
  [REPORTING_DOCUMENT_TYPES.subordinationAgreements]: 'Subordination Agreements',
  [REPORTING_DOCUMENT_TYPES.warehouseWaivers]: 'Warehouse Waivers',
  [REPORTING_DOCUMENT_TYPES.signatureAuths]: 'Signature Auths',
  [REPORTING_DOCUMENT_TYPES.boardConsent]: 'Board Consent',
}

const REPORTING_DOCUMENT_DISPLAY_NAMES: Partial<Record<string, string>> = {
  [REPORTING_DOCUMENT_TYPES.ucc]: UCC_SEARCH_LABEL,
}

/** On-screen name for a stored document name / ongoing reporting type; unmapped names pass through. */
export const getReportingDocumentLabel = (documentName: string): string =>
  REPORTING_DOCUMENT_DISPLAY_NAMES[documentName] ?? documentName

const UCC_SEARCH_LABEL_PATTERN = /\bUCC Search(es)?\b/gi

/** Maps the on-screen "UCC Search" back to the indexed 'UCC' type so searching the label finds it. */
export const toStoredReportingDocumentSearch = (search: string): string =>
  search.replace(UCC_SEARCH_LABEL_PATTERN, REPORTING_DOCUMENT_TYPES.ucc)

export const REPORTING_DOCUMENT_FREQUENCY = {
  [REPORTING_DOCUMENT_TYPES.apAging]: [FREQUENCY_TYPES.atFunding, FREQUENCY_TYPES.monthly],
  [REPORTING_DOCUMENT_TYPES.arAging]: [FREQUENCY_TYPES.atFunding, FREQUENCY_TYPES.monthly],
  [REPORTING_DOCUMENT_TYPES.inventoryDetail]: [FREQUENCY_TYPES.atFunding, FREQUENCY_TYPES.monthly],
  [REPORTING_DOCUMENT_TYPES.arGeneralLedger]: [FREQUENCY_TYPES.quarterly],
  [REPORTING_DOCUMENT_TYPES.salesBySKU]: [FREQUENCY_TYPES.quarterly],
  [REPORTING_DOCUMENT_TYPES.financials]: [FREQUENCY_TYPES.monthly],
  [REPORTING_DOCUMENT_TYPES.financialProjections]: [FREQUENCY_TYPES.semiAnnually],
  [REPORTING_DOCUMENT_TYPES.capTable]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.bankTransactions]: [FREQUENCY_TYPES.monthly],
  [REPORTING_DOCUMENT_TYPES.ucc]: [FREQUENCY_TYPES.semiAnnually],
  [REPORTING_DOCUMENT_TYPES.gs]: [FREQUENCY_TYPES.semiAnnually],
  [REPORTING_DOCUMENT_TYPES.coi]: [FREQUENCY_TYPES.annually],
  [REPORTING_DOCUMENT_TYPES.eop]: [FREQUENCY_TYPES.annually],
  [REPORTING_DOCUMENT_TYPES.lsa]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.ipsa]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.noticeOfIpsa]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.lienFilings]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.corporateGuaranteesAndPledges]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.supportGuarantee]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.subordinationAgreements]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.warehouseWaivers]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.signatureAuths]: [FREQUENCY_TYPES.adHoc],
  [REPORTING_DOCUMENT_TYPES.boardConsent]: [FREQUENCY_TYPES.adHoc],
}

export const DUE_DILIGENCE_DEFAULT_REQUIRED_REPORTS: Array<{
  documentName: string
  frequency: string[]
  clientFacing: false
}> = Object.values(LEGAL_DOCUMENT_TYPES).map((value) => ({
  documentName: value,
  frequency: REPORTING_DOCUMENT_FREQUENCY[value],
  clientFacing: false,
}))

export const AGGREGATION_TYPES = {
  check: 'Check',
  wire: 'Wire',
  adjustment: 'Adjustment',
  fee: 'Fee',
  wireFee: 'Wire Fee',
  facilityFee: 'Facility Fee',
  lockboxFee: 'Lockbox Fee',
  terminationFee: 'Termination Fee',
  funding: 'Funding',
  passThroughs: 'Pass-Through',
  interestAmountMonthly: 'Interest Fee',
  unusedLineFee: 'Unused Line Fee',
  principal: 'Principal',
}

export enum ClientAggregationTypes {
  FeesAdjustments = 'Fees & Adjustments',
  Collections = 'Collections',
  Funding = 'Funding',
}

export const CLIENT_AGGREGATION_TYPES = [
  {
    value: ClientAggregationTypes.FeesAdjustments,
    label: ClientAggregationTypes.FeesAdjustments,
  },
  {
    value: ClientAggregationTypes.Collections,
    label: ClientAggregationTypes.Collections,
  },
  {
    value: ClientAggregationTypes.Funding,
    label: ClientAggregationTypes.Funding,
  },
]
export const CLIENT_AGGREGATION_TYPE_MAPPING: Partial<Record<ClientAggregationTypes, string[]>> = {
  [ClientAggregationTypes.FeesAdjustments]: [
    AGGREGATION_TYPES.fee,
    AGGREGATION_TYPES.wireFee,
    AGGREGATION_TYPES.facilityFee,
    AGGREGATION_TYPES.lockboxFee,
    AGGREGATION_TYPES.terminationFee,
    AGGREGATION_TYPES.adjustment,
    AGGREGATION_TYPES.interestAmountMonthly,
    AGGREGATION_TYPES.principal,
    AGGREGATION_TYPES.unusedLineFee,
  ],
  [ClientAggregationTypes.Collections]: [AGGREGATION_TYPES.check, AGGREGATION_TYPES.wire],
  [ClientAggregationTypes.Funding]: [AGGREGATION_TYPES.funding, AGGREGATION_TYPES.passThroughs],
}

export const CLIENT_INTAKE_STEPS = {
  GET_STARTED: 0,
  COMPANY_INFO: 1,
  PEOPLE: 2,
  DOCUMENTATION: 3,
}

export const CLIENT_INTAKE_STEPS_LIST = Object.values(CLIENT_INTAKE_STEPS).filter(
  (step) => step !== CLIENT_INTAKE_STEPS.GET_STARTED,
)

export const CLIENT_INTAKE_HEADINGS = {
  [CLIENT_INTAKE_STEPS.GET_STARTED]: {
    title: 'Let’s get started',
    description: 'Fill out this brief form so we can find the best solution for your capital needs',
  },
  [CLIENT_INTAKE_STEPS.COMPANY_INFO]: {
    title: 'Company info',
    description: 'Let’s hear a bit about your business',
  },
  [CLIENT_INTAKE_STEPS.PEOPLE]: {
    title: 'People & tools',
    description: 'Tell us about the people and systems behind your business',
  },
  [CLIENT_INTAKE_STEPS.DOCUMENTATION]: {
    title: 'Documentation',
    description: 'Please upload docs below in Excel format',
  },
}

export const CLIENT_ENTITY_RELATIONSHIP_TYPE_SALESFORCE_DESCRIPTION: Partial<
  Record<ClientInfoStatus, Partial<Record<ClientEntityRelationshipType, string>>>
> = {
  Prospect: {
    [ClientEntityRelationshipType.LawFirm]: 'Added from Client Intake: Law Firm',
    [ClientEntityRelationshipType.OutsourcedCFO]: 'Added from Client Intake: Outsourced CFO',
    [ClientEntityRelationshipType.AccountingFirm]: 'Added from Client Intake: Accounting firm',
    [ClientEntityRelationshipType.DistributionServices3PL]: 'Added from Client Intake: 3PL',
  },
  'Due Diligence': {
    [ClientEntityRelationshipType.LawFirm]: 'Added from Underwriting application: Law Firm',
    [ClientEntityRelationshipType.OutsourcedCFO]:
      'Added from Underwriting application: Outsourced CFO',
    [ClientEntityRelationshipType.AccountingFirm]:
      'Added from Underwriting application: Accounting firm',
    [ClientEntityRelationshipType.DistributionServices3PL]:
      'Added from Underwriting application: 3PL',
  },
}

export const LAST_NOTIFICATIONS_COUNT = 5

export enum CollateralTypes {
  ar = 'AR',
  ap = 'AP',
  inventory = 'Inventory',
}

export const OVERPAYABLE_INTERVAL_VALUES = [
  {
    value: '0',
    label: 'All',
  },
  {
    value: '30',
    label: '30',
  },
  {
    value: '60',
    label: '60',
  },
  {
    value: '90',
    label: '90',
  },
]

export const VENDOR_TYPE_VALUES = [
  { value: 'BNPL', label: 'BNPL' },
  { value: 'Payment Processor', label: 'Payment Processor' },
]

export const MONTHS_OF_SALES = 12

export const CLIENT_CASH_FLOW_FIELDS = [
  'beginingCash',
  'avgNetIncome',
  'operatingActivities',
  'changeInAccountsReceivableNet',
  'changeInInventory',
  'changeInPrepaidExpenses',
  'changeInAccountsPayable',
  'changeInAccruedExpenses',
  'investingActivities',
  'changeInNetPpe',
  'financingActivities',
  'changeInAdditionalPaidInCapital',
  'changeInConvertibleNotesDebt',
  'changeInConvertibleNotesEquity',
  'changeInDwightFundingLoan',
  'changeInOtherShortTermDebt',
  'changeInOtherLongTermDebt',
  'cashFlow',
  'changeInOther',
  'changeInCash',
  'endingCash',
  'avgOperatingCashFlow',
  'monthsOfCashOnHandOperatingCashFlow',
  'avgEBITDA',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_HIGHLIGHTED_FIELDS = [
  'avgOperatingCashFlow',
  'avgEBITDA',
  'monthsOfCashOnHandOperatingCashFlow',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_NUMBER_FIELDS = [
  'monthsOfCashOnHandOperatingCashFlow',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_HEADER_FIELDS = [
  'operatingActivities',
  'investingActivities',
  'financingActivities',
  'cashFlow',
  'monthsOfCashOnHandOperatingCashFlow',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_INDENT_FIELDS = [
  'changeInAccountsReceivableNet',
  'changeInInventory',
  'changeInPrepaidExpenses',
  'changeInAccountsPayable',
  'changeInAccruedExpenses',
  'changeInNetPpe',
  'changeInAdditionalPaidInCapital',
  'changeInConvertibleNotesDebt',
  'changeInConvertibleNotesEquity',
  'changeInDwightFundingLoan',
  'changeInOtherShortTermDebt',
  'changeInOtherLongTermDebt',
  'changeInOther',
  'changeInCash',
]

export const CLIENT_CASH_FLOW_GRAPH_FIELDS = [
  'avgOperatingCashFlow',
  'monthsOfCashOnHandOperatingCashFlow',
  'avgEBITDA',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_GRAPH_FIELDS_COLORS: Record<string, string> = {
  avgOperatingCashFlow: '#395580',
  monthsOfCashOnHandOperatingCashFlow: '#718096',
  avgEBITDA: '#CBD5E0',
  monthsOfCashOnHandEBITDA: '#0066f5',
}

export const CLIENT_CASH_FLOW_GRAPH_LINE_FIELDS = [
  'monthsOfCashOnHandOperatingCashFlow',
  'monthsOfCashOnHandEBITDA',
]

export const CLIENT_CASH_FLOW_FIELDS_LABELS: Record<string, string> = {
  beginingCash: 'Starting Cash',
  avgNetIncome: 'Net Income',
  operatingActivities: 'Operating Activities',
  changeInAccountsReceivableNet: 'Accounts Receivable',
  changeInInventory: 'Inventory',
  changeInPrepaidExpenses: 'Prepaid Expenses',
  changeInAccountsPayable: 'Accounts Payable',
  changeInAccruedExpenses: 'Accrued Expenses',
  investingActivities: 'Investing Activities',
  changeInNetPpe: 'Net PPE',
  financingActivities: 'Financing Activities',
  changeInAdditionalPaidInCapital: 'Additional Paid in Capital',
  changeInConvertibleNotesDebt: 'Convertible Notes Debt',
  changeInConvertibleNotesEquity: 'Convertible Notes Equity',
  changeInDwightFundingLoan: 'Dwight Funding Loan',
  changeInOtherShortTermDebt: 'Other Short Term Debt',
  changeInOtherLongTermDebt: 'Other Long Term Debt',
  cashFlow: 'Cash Flow',
  changeInOther: 'Change in Other',
  changeInCash: 'Net Change in Cash',
  endingCash: 'Ending Cash',
  avgOperatingCashFlow: 'Operating Cash Flow',
  monthsOfCashOnHandOperatingCashFlow: 'Months of Cash On-hand (Operating Cash Flow)',
  avgEBITDA: 'EBITDA',
  monthsOfCashOnHandEBITDA: 'Months of Cash On-hand (EBITDA)',
}

export const CLIENT_AR_ROLLFORWARD_GRAPH_FIELDS = [
  'newInvoices',
  'actualCollections',
  'impliedCollections',
  'impliedDilution',
]

export const CLIENT_AR_ROLLFORWARD_FIELDS_LABELS: Record<string, string> = {
  newInvoices: 'New Invoices',
  actualCollections: 'Actual Collections',
  impliedCollections: 'Implied Collections',
  impliedDilution: 'Implied Dilution',
}

export const CLIENT_AR_ROLLFORWARD_GRAPH_FIELDS_COLORS: Record<string, string> = {
  newInvoices: '#718096',
  actualCollections: '#395580',
  impliedCollections: '#CBD5E0',
  impliedDilution: '#0066f5',
}

export const CLIENT_AR_ROLLFORWARD_GRAPH_LINE_FIELDS = ['impliedDilution']

export enum FundingStep {
  RequestReceived = 'Request Received',
  UnderReview = 'Under Review',
  SentForSignature = 'Sent for Signature',
  FundingInProgress = 'Funding in Progress',
}

export enum FundingStepCompleted {
  RequestReceived = 'Request Received',
  UnderReview = 'Under Review',
  SentForSignature = 'Sent for Signature',
  Completed = 'Completed',
}

export const FUNDING_STEPS = [
  FundingStep.RequestReceived,
  FundingStep.UnderReview,
  FundingStep.SentForSignature,
  FundingStep.FundingInProgress,
] as const

export const FUNDING_STEPS_COMPLETED = [
  FundingStepCompleted.RequestReceived,
  FundingStepCompleted.UnderReview,
  FundingStepCompleted.SentForSignature,
  FundingStepCompleted.Completed,
] as const

// File upload size limit set to 150 MB — nginx ingress proxy-body-size must be >= this value
export const MAX_FILE_SIZE = 150 * 1000 * 1000

// Largest feedback screenshot accepted, measured after base64 decoding.
export const MAX_SCREENSHOT_SIZE = 10 * 1024 * 1024

// Cap for non-file multipart fields, which busboy otherwise caps at 1 MB and aborts past.
// Feedback screenshots ride as base64 text fields, so this must clear MAX_SCREENSHOT_SIZE
// plus base64's 4/3 inflation.
export const MAX_FORM_FIELD_SIZE = Math.ceil((MAX_SCREENSHOT_SIZE * 4) / 3) + 1024

// Cap on a file attached to a message or comment, enforced by the composer's dropzone and
// by the server's storeAttachment.
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024

// Videos (Team Messaging only) get a larger cap: screen recordings run big. Must stay under
// MAX_FILE_SIZE, and the bytes come back as a base64 data URL, a third larger again.
export const VIDEO_ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024

// The video formats Team Messaging accepts, in react-dropzone's `accept` shape. `.mov` is
// what an iPhone records.
export const VIDEO_ATTACHMENT_ACCEPT: Record<string, string[]> = {
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/quicktime': ['.mov'],
}

// Max size for an inline image pasted/dropped into a task description or comment
// (stored in S3, referenced by key from the content). Capped below the general
// attachment limit; enforced on the client (paste/drop) and the upload endpoint.
export const INLINE_IMAGE_MAX_BYTES = 5 * 1000 * 1000

// Max size for a channel attachment that renders inline in the message timeline,
// where nobody asked for it — the bytes are fetched as soon as the row scrolls
// into view. The right trade for a screenshot, the wrong one for a large
// workbook, so above this the attachment stays a chip and fetches only when
// opened. The full-screen preview has no cap: opening it is the request.
//
// Applies to images as much as to documents. It originally gated only PDFs and
// spreadsheets, which left the one file type people actually share ungated: an
// image was fetched inline at any size up to the 25 MB upload limit, and arrives
// as a base64 `data:` URL roughly a third larger again.
export const INLINE_PREVIEW_MAX_BYTES = 2 * 1000 * 1000

// The downscaled JPEG the composer uploads beside an image over INLINE_PREVIEW_MAX_BYTES,
// so the timeline can show it without fetching the original.
export const IMAGE_PREVIEW_MAX_EDGE_PX = 1024
export const IMAGE_PREVIEW_QUALITY = 0.8

/** Box ids of the sample insurance documents previewed in the client reporting flow. */
export const SAMPLE_INSURANCE_BOX_FILE_IDS: Record<
  'production' | 'other',
  Record<string, string>
> = {
  production: {
    [REPORTING_DOCUMENT_TYPES.coi]: '1726625903534',
    [REPORTING_DOCUMENT_TYPES.eop]: '1726628574213',
  },
  other: {
    [REPORTING_DOCUMENT_TYPES.coi]: '1726634855740',
    [REPORTING_DOCUMENT_TYPES.eop]: '1726634862940',
  },
}
